import { memo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { Rss, Bookmark, Share2, FileText, Settings, PanelLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserProfile } from './UserProfile'
import { ROUTES } from '@/app/routes'
import {
  useActiveTopic,
  getLastViewedTopicId,
} from '@/features/topics/state/useActiveTopic'

const ICON_SIZE = 20
const ICON_STROKE = 2

type NavItemDef = {
  key: 'feed' | 'saved' | 'network' | 'reports' | 'settings'
  icon: React.ElementType
}

type NavigationSidebarProps = {
  mobileOpen: boolean
  onMobileClose: () => void
}

const NAV_ITEMS: NavItemDef[] = [
  { key: 'feed', icon: Rss },
  { key: 'saved', icon: Bookmark },
  { key: 'network', icon: Share2 },
  { key: 'reports', icon: FileText },
  { key: 'settings', icon: Settings },
]

function resolveTopicPath(
  builder: (id: string | number) => string,
  topicId: string | null,
): string {
  const id = topicId ?? getLastViewedTopicId()
  if (id) return builder(id)
  return ROUTES.app.topicsList
}

function buildPath(key: NavItemDef['key'], topicId: string | null): string {
  if (key === 'feed') return resolveTopicPath(ROUTES.app.topicFeed, topicId)
  if (key === 'saved') return resolveTopicPath(ROUTES.app.topicSaved, topicId)
  if (key === 'network') return resolveTopicPath(ROUTES.app.topicNetwork, topicId)
  if (key === 'reports') return resolveTopicPath(ROUTES.app.topicReports, topicId)
  return ROUTES.app.settings
}

function matchesRoute(key: NavItemDef['key'], pathname: string): boolean {
  if (key === 'settings') return pathname.startsWith(ROUTES.app.settings)
  if (key === 'feed') return /\/app\/topics\/[^/]+\/feed/.test(pathname)
  if (key === 'saved') return /\/app\/topics\/[^/]+\/saved/.test(pathname)
  if (key === 'network') return /\/app\/topics\/[^/]+\/network/.test(pathname)
  if (key === 'reports') return /\/app\/topics\/[^/]+\/reports/.test(pathname)
  return false
}

export const NavigationSidebar = memo(function NavigationSidebar({
  mobileOpen,
  onMobileClose,
}: NavigationSidebarProps) {
  const { t } = useTranslation('sidebar')
  const { t: tCommon } = useTranslation()
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const { topicId } = useActiveTopic()
  const [manualCollapse, setManualCollapse] = useState<boolean | null>(null)

  const isCollapsed = manualCollapse !== null && isDesktop ? manualCollapse : !isDesktop

  const showLabels = mobileOpen || !isCollapsed

  const sidebarWidthClass = isCollapsed
    ? 'w-[80px] min-w-[80px]'
    : 'w-[280px] min-w-[280px]'

  return (
    <>
      <nav
        className={cn(
          'print-hide flex h-full flex-col gap-2 overflow-hidden border-r border-border transition-all duration-300 ease-in-out',
          'bg-background/95 backdrop-blur-xl',
          'hidden lg:flex',
          sidebarWidthClass,
          mobileOpen && 'fixed inset-y-0 left-0 z-40 !flex w-[280px] min-w-[280px]',
        )}
      >
        <div className="hidden flex-col p-2 pb-0 lg:flex">
          <button
            type="button"
            onClick={() => setManualCollapse(!isCollapsed)}
            className="flex h-11 w-full items-center rounded-lg px-2 text-muted-foreground transition-all hover:bg-secondary/50 hover:text-foreground"
            title={isCollapsed ? tCommon('common.expand') : tCommon('common.collapse')}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center">
              <PanelLeft size={ICON_SIZE} strokeWidth={ICON_STROKE} />
            </div>
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
          {NAV_ITEMS.map((item) => {
            const active = matchesRoute(item.key, location.pathname)
            const path = buildPath(item.key, topicId)
            const Icon = item.icon
            const label = t(`item.${item.key}`)
            return (
              <Link
                key={item.key}
                to={path}
                onClick={onMobileClose}
                title={!showLabels ? label : undefined}
                className={cn(
                  'flex items-center rounded-lg py-2 text-[15px] transition-all duration-150 active:scale-[0.97] active:bg-nav-active-surface',
                  active
                    ? 'border-l-2 border-nav-active-border bg-nav-active-surface font-semibold text-foreground'
                    : 'border-l-2 border-transparent text-muted-foreground hover:bg-secondary/50',
                  'px-2',
                )}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center transition-[filter] duration-200',
                    active &&
                      'text-nav-active drop-shadow-[0_0_6px_var(--nav-active-glow)]',
                  )}
                >
                  <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} />
                </div>
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    showLabels ? 'ml-2 max-w-[200px] opacity-100' : 'max-w-0 opacity-0',
                  )}
                >
                  <span className="whitespace-nowrap">{label}</span>
                </div>
              </Link>
            )
          })}
        </div>

        <UserProfile
          isCollapsed={mobileOpen ? false : isCollapsed}
          onRequestExpand={() => setManualCollapse(false)}
        />
      </nav>

      {mobileOpen && (
        <div
          role="presentation"
          className="fixed inset-0 z-30 bg-background/50 lg:hidden"
          onClick={onMobileClose}
        />
      )}
    </>
  )
})
